/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import filter from 'lodash/filter';

import {
    SecurityHelper,
    Box,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import AvailabilityMonths from 'ui-permit/shared-components/detailed-availability/AvailabilityMonthsNew';
import { RecaptchaCheckbox, withRecaptchaFallback } from 'shared-ui/recaptcha';
import { SharedCaptchaModal } from 'shared-ui/components/RecaptchaModal/SharedCaptchaModal';
import { authModalActions } from 'shared-ui/auth';
import OvernightAvailabilityGrid from '../components/OvernightAvailabilityGrid';
import AvailabilityNavigator from '../components/AvailabilityNavigator';
import * as aActions from '../actions/availability';
import * as pActions from '../actions/permit';
import BookNowComponent from '../components/BookNowComponent';
import * as types from '../constants';
import { issuanceStatuses, formatErrorMessage } from '../utils/permitUtilsNew';
import {
    getGateA,
    RECAPTCHA_ACTION_CREATE_ISSUANCE,
    RECAPTCHA_FAILED_MESSAGE,
} from '../utils/recaptcha';

export class AvailabilityOvernightMotorContainer extends React.Component {
    static displayName = 'AvailabilityOvernightMotorContainer';

    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        permitId: PropTypes.string,
        permitDate: PropTypes.string,
        availabilities: PropTypes.object,
        issuance: PropTypes.object,
        existingIssuance: PropTypes.object,
        divisions: PropTypes.object,
        selectedStartDate: PropTypes.string,
        selectedEndDate: PropTypes.string,
        selectedDivision: PropTypes.object,
        divisionSearchCriteria: PropTypes.object,
        error: PropTypes.string,
        dispatch: PropTypes.func,
        issuanceLoading: PropTypes.bool,
        isSmallerGrid: PropTypes.bool,
        numOfCols: PropTypes.number,
        daysToSkip: PropTypes.number,
        isFullView: PropTypes.bool,
        account: PropTypes.object,
        handleDateChange: PropTypes.func,
        recaptcha: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            permitId: props.permitId,
            permitDate: props.permitDate,
            divisions: this.getFilteredDivisions(
                props.divisions,
                props.divisionSearchCriteria
            ),
            isMessageModalOpen: false,
            message: null,
            messageTitle: null,
            waitingForUserAccount: false,
            isChallengeModalOpen: false,
        };
        this.onNavigatorDateChanged = this.onNavigatorDateChanged.bind(this);
        this.onSelectToggle = this.onSelectToggle.bind(this);
        this.onCloseMessageModal = this.onCloseMessageModal.bind(this);
        this.onClearDates = this.onClearDates.bind(this);
        this.onBook = this.onBook.bind(this);
    }

    componentDidMount() {
        this.refreshAvailability();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            nextProps.existingIssuance &&
            get(this.props, 'existingIssuance.id', null) !== nextProps.existingIssuance.id
        ) {
            this.refreshAvailability(nextProps.existingIssuance);
        }
        if (
            nextProps.permitDate !== this.state.permitDate ||
            JSON.stringify(nextProps.divisions) !==
                JSON.stringify(this.props.divisions) ||
            JSON.stringify(nextProps.divisionSearchCriteria) !==
                JSON.stringify(this.props.divisionSearchCriteria)
        ) {
            this.setState(
                {
                    permitId: nextProps.permitId,
                    permitDate: nextProps.permitDate,
                    divisions: this.getFilteredDivisions(
                        nextProps.divisions,
                        nextProps.divisionSearchCriteria
                    ),
                },
                () => {
                    this.refreshAvailability(nextProps.existingIssuance);
                }
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (
            SecurityHelper.isLoggedIn() &&
            this.state.waitingForUserAccount &&
            this.props.account &&
            prevProps.account !== this.props.account
        ) {
            this.setState({ waitingForUserAccount: false });
            this.onBook();
        }
    }

    onNavigatorDateChanged(dates) {
        this.props.handleDateChange(dates.startDate);
    }

    onSelectToggle(division, startDate) {
        if (
            this.props.selectedStartDate === startDate &&
            this.props.selectedDivision &&
            this.props.selectedDivision.id === division.id
        ) {
            this.props.dispatch(pActions.setSelectedStartDate(null));
        } else {
            this.props.dispatch(pActions.setSelectedStartDate(startDate));
        }
        this.props.dispatch(pActions.setSelectedDivision(division));
        this.props.dispatch(pActions.setSelectedEndDate(null));
    }

    onClearDates() {
        this.props.dispatch(pActions.setSelectedStartDate(null));
        this.props.dispatch(pActions.setSelectedEndDate(null));
    }

    onCloseMessageModal() {
        this.setState({
            isMessageModalOpen: false,
            message: null,
            messageTitle: null,
        });
    }

    async onBook() {
        const loggedIn = SecurityHelper.isLoggedIn();

        if (!loggedIn) {
            this.setState({ waitingForUserAccount: true });
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }

        // A v2 challenge was previously required - show the checkbox modal before
        // resubmitting. Once open, fall through and submit the v2 token.
        if (
            this.props.recaptcha.isChallengeRequired &&
            !this.state.isChallengeModalOpen
        ) {
            this.setState({ isChallengeModalOpen: true });
            return;
        }

        const permitId = types.BOUNDARY_WATERS_PERMIT_ID;
        const item = {
            permit_id: permitId,
            permit_type: types.TYPE_OVERNIGHT_MOTOR,
            division_id: this.props.selectedDivision.id,
            user_id: SecurityHelper.getAccount().account_id,
            start_date: gnoment(this.props.selectedStartDate),
            status: issuanceStatuses.get(types.PERMIT_PRELIMINARY),
            equipments: [
                {
                    type: types.EQUIP_TYPE_MOTORBOAT,
                    qty: 1,
                },
            ],
        };
        if (this.props.existingIssuance) {
            const newItem = {
                ...this.props.existingIssuance,
                ...item,
                id: this.props.existingIssuance.id,
                status: this.props.existingIssuance.status,
                is_modify_quota: true,
            };
            this.props.dispatch(pActions.updateIssuance(newItem)).then(() => {
                this.navigateToRegistration(this.props.existingIssuance, permitId);
            });
        } else {
            const gate_a = await getGateA(
                this.props.recaptcha,
                RECAPTCHA_ACTION_CREATE_ISSUANCE
            );
            try {
                await this.props.dispatch(
                    pActions.addItem(types.PERMIT, item, false, gate_a)
                );
                this.setState({ isChallengeModalOpen: false });
                this.navigateToRegistration(this.props.issuance, permitId);
            } catch (error) {
                const { shouldRetry, isFailed } =
                    this.props.recaptcha.handleRecaptchaError(error);
                if (shouldRetry) {
                    this.setState({ isChallengeModalOpen: true });
                    return;
                }
                if (isFailed) {
                    this.setState({
                        isChallengeModalOpen: false,
                        isMessageModalOpen: true,
                        message: RECAPTCHA_FAILED_MESSAGE,
                        messageTitle: 'Error',
                    });
                    return;
                }
                // Non-recaptcha errors already surface via navigateToRegistration's
                // props.error path on the next render; keep prior behavior by
                // routing through it.
                this.setState({ isChallengeModalOpen: false });
                this.navigateToRegistration(this.props.issuance, permitId);
            }
        }
    }

    getFilteredDivisions(divisionObjs, searchCriteria) {
        const ret = [];
        Object.keys(divisionObjs).forEach((id) => {
            const division = divisionObjs[id];
            if (division.type === types.DIV_TYPE_OM) {
                ret[ret.length] = divisionObjs[id];
            }
        });
        return this.applySearchCriteria(ret, searchCriteria);
    }

    handleDivisionDetailsClick = (divisionId) => {
        this.props.dispatch(pActions.openDivisionDetailsModal(divisionId));
    };

    refreshAvailability(existingIssuance) {
        if (this.state.permitId && this.state.permitDate) {
            const startDate = gnoment(this.state.permitDate);
            this.props.dispatch(
                aActions.fetchAvailability(
                    this.state.permitId,
                    startDate.clone().add(-7, 'day').toDate(),
                    startDate.clone().add(10, 'day').toDate(),
                    startDate.clone().utc().startOf('day').toISOString(),
                    existingIssuance ? existingIssuance.id : null
                )
            );
        }
    }

    applySearchCriteria(divisionObjs, searchCriteria) {
        let ret = divisionObjs;
        if (searchCriteria) {
            if (searchCriteria.searchText && searchCriteria.searchText !== '') {
                const searchText = searchCriteria.searchText.toLowerCase();
                ret = ret.filter((item) =>
                    `${item.name}  ${item.district}`.toLowerCase().includes(searchText)
                );
            }
            if (searchCriteria.motorSize) {
                ret = ret.filter((item) => {
                    return item.motor_hp_limit <= searchCriteria.motorSize;
                });
            }
            if (searchCriteria.areas.length > 0) {
                const retFiltered = [];
                forEach(ret, (item) => {
                    filter(
                        searchCriteria.areas,
                        (area) => item.district.includes(area) && retFiltered.push(item)
                    );
                    return retFiltered;
                });
                ret = retFiltered;
            }
        }
        return ret;
    }

    navigateToRegistration(issuance, permitId) {
        if (!this.props.error) {
            const reservationId = `${issuance.id}${types.RES_ID_DELIMITER}${permitId}`;
            const redirectURL = `/permits/${permitId}/registration/${reservationId}/select-cooperator`;
            this.props.router.navigate(redirectURL);
        } else {
            const errStr = formatErrorMessage(`${this.props.error}`);
            this.setState({
                isMessageModalOpen: true,
                message: errStr,
                messageTitle: 'Error',
            });
        }
    }

    renderAvailabilityNavigator = () => {
        const { daysToSkip, permitDate } = this.props;
        return (
            <AvailabilityNavigator
                startDate={gnoment(permitDate)}
                endDate={gnoment(permitDate).add(daysToSkip, 'day')}
                onDatesChange={this.onNavigatorDateChanged}
                handleClearDatesClick={this.onClearDates}
                daysToSkip={daysToSkip}
            />
        );
    };

    render() {
        let modalMessages = null;
        if (this.state.message) {
            modalMessages = [];
            const arr = this.state.message.split('\n');
            arr.forEach((msg, index) => {
                modalMessages.push(<li key={index}>{msg}</li>);
            });
        }

        return (
            <div className="per-availability-content">
                <p>
                    Any group taking an overnight motor trip from May 1 - September 30
                    needs a quota permit. Groups may only enter the BWCAW on the entry
                    date and through the entry point specified on the permit.
                </p>
                <p>
                    Select permit type and entry date from the filters above to begin.
                    Click on filter box to narrow search.
                </p>

                {this.renderAvailabilityNavigator()}
                <AvailabilityMonths
                    numOfCols={this.props.numOfCols}
                    startDate={this.props.permitDate}
                />
                <div className="per-availability-table-container">
                    <OvernightAvailabilityGrid
                        divisions={this.state.divisions}
                        availabilities={this.props.availabilities}
                        permitDate={this.props.permitDate}
                        onDivisionDetailsClick={this.handleDivisionDetailsClick}
                        onSelectToggle={this.onSelectToggle}
                        isSmallerGrid={this.props.isSmallerGrid}
                        numOfCols={this.props.numOfCols}
                        isFullView={this.props.isFullView}
                        selectedDivision={this.props.selectedDivision}
                        selectedStartDate={this.props.selectedStartDate}
                    >
                        <Box className="pt-1" background="white">
                            {this.renderAvailabilityNavigator()}
                            <AvailabilityMonths
                                numOfCols={this.props.numOfCols}
                                startDate={this.props.permitDate}
                            />
                        </Box>
                    </OvernightAvailabilityGrid>
                </div>

                <BookNowComponent
                    selectedDivision={this.props.selectedDivision}
                    selectedStartDate={this.props.selectedStartDate}
                    selectedEndDate={this.props.selectedEndDate}
                    onBookClick={this.onBook}
                    isEnabled={
                        !this.props.issuanceLoading &&
                        this.props.selectedDivision !== null &&
                        this.props.selectedStartDate !== null
                    }
                />

                <SharedCaptchaModal
                    isOpen={this.state.isChallengeModalOpen}
                    onRequestClose={() => this.setState({ isChallengeModalOpen: false })}
                    onContinue={this.onBook}
                    heading="Confirm You're Not a Robot"
                    showCancelButton
                    isContinueDisabled={this.props.recaptcha.isV2Invalid}
                    isContinueLoading={this.props.issuanceLoading}
                >
                    Please complete the challenge below to continue with your reservation.
                    <RecaptchaCheckbox
                        {...this.props.recaptcha.recaptchaCheckboxProps}
                        isFallbackOnly
                        action={RECAPTCHA_ACTION_CREATE_ISSUANCE}
                    />
                </SharedCaptchaModal>

                <StyledModal
                    shouldShowCloseButton={false}
                    shouldActionsStickInMobile
                    size="md"
                    isOpen={this.state.isMessageModalOpen}
                    heading={this.state.messageTitle}
                    onRequestClose={this.onCloseMessageModal}
                >
                    {modalMessages}

                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                onClick={this.onCloseMessageModal}
                                appearance="tertiary"
                            >
                                Close
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}

function select(state) {
    return {
        account: state.auth.account,
        permitId: state.boundarywaters.permit.result.id,
        permitDate: state.boundarywaters.permitDate,
        selectedDivision: state.boundarywaters.selectedDivision,
        selectedStartDate: state.boundarywaters.selectedStartDate,
        availabilities: state.boundarywaters.permit.availabilities,
        permit: state.boundarywaters.permit.result,
        divisions: state.boundarywaters.permit.result.divisions,
        issuance: state.boundarywaters.permit.issuance,
        error: state.boundarywaters.permit.error,
        divisionSearchCriteria: state.boundarywaters.divisionSearchCriteria,
        existingIssuance: state.boundarywaters.permit.existingIssuance,
        issuanceLoading: state.boundarywaters.issuanceLoading,
    };
}

export default withCompatRouter(
    connect(select)(withRecaptchaFallback(AvailabilityOvernightMotorContainer))
);

// cSpell:ignore boundarywaters, BWCAW
